import { Delegate } from './delegate.js';

const MOBILE_LAYOUT_QUERY = 'screen and (max-width: 767px)';
const USP_AUTOPLAY_DELAY_MS = 4000;
const USP_AUTOPLAY_RESUME_MS = 4000;

if (!customElements.get('product-usp-slider')) {
  customElements.define(
    'product-usp-slider',
    class ProductUspSlider extends HTMLElement {
      /** Initializes instance fields used by lifecycle methods. */
      constructor() {
        super();
        this.delegate = null;
        this.equalizeTimer = null;
        this.initRaf = null;
        this.list = null;
        this.mql = null;
        this.onBreakpointChange = (event) => {
          window.clearTimeout(this.resizeTimer);
          this.resizeTimer = null;
          this.syncSlider(event.matches);
        };
        this.onResize = this.queueSyncSlider.bind(this);
        this.resizeObserver = null;
        this.resizeTimer = null;
        this.sliderInstance = null;
        this.wrap = null;
        this.autoplayResumeTimer = null;
        this.autoplayInteractionBinding = null;
        this.slideCount = 0;
      }

      /** Sets up DOM references, delegated events, observers, and initial breakpoint sync. */
      connectedCallback() {
        this.wrap = this.querySelector('.product__usp-wrap');
        this.list = this.querySelector('.product__usp-list');
        this.sliderInstance = null;
        this.slideCount = this.countOriginalSlides();
        this.sliderOptions = this.parseSliderOptions();

        this.equalizeHeights = this.equalizeHeights.bind(this);
        this.delegate = new Delegate(this);
        this.delegate.on('keypress', '.product__usp-nav', this.onNavKeypress);
        this.delegate.on('click', '.product__usp-nav', this.onNavClick);

        this.mql = this.getMobileMediaQueryList();
        if (this.mql?.addEventListener) {
          this.mql.addEventListener('change', this.onBreakpointChange);
        } else if (this.mql?.addListener) {
          this.mql.addListener(this.onBreakpointChange);
        }
        window.addEventListener('resize', this.onResize, { passive: true }); // delegate-exempt: viewport resize has no DOM target.

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => this.queueEqualizeHeights());
          this.resizeObserver.observe(this);
        }

        this.syncSlider();
      }

      /** Tears down listeners, observers, queued work, and any active carousel. */
      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        if (this.mql?.removeEventListener) {
          this.mql.removeEventListener('change', this.onBreakpointChange);
        } else if (this.mql?.removeListener) {
          this.mql.removeListener(this.onBreakpointChange);
        }
        this.resizeObserver?.disconnect();
        this.delegate?.destroy();
        this.cancelQueuedWork();
        this.destroySlider();
        this.delegate = null;
        this.mql = null;
        this.resizeObserver = null;
      }

      /** Applies the correct static or carousel layout for the current breakpoint. */
      syncSlider(forceMobile = null) {
        if (!this.isConnected) return;
        this.cancelQueuedWork();
        if (!this.isThemeReady()) {
          this.queueSyncFrame();
          return;
        }

        if (!this.wrap || !this.list || this.slideCount < 2) {
          this.destroySlider();
          this.clearEqualizedHeights();
          return;
        }

        const isMobile = typeof forceMobile === 'boolean' ? forceMobile : this.isMobileLayout();
        if (isMobile) {
          this.initSlider();
        } else {
          this.destroySlider();
        }

        this.equalizeHeights();
      }

      /** Debounces resize-driven layout synchronization. */
      queueSyncSlider() {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          this.resizeTimer = null;
          this.syncSlider();
        }, 100);
      }

      /** Debounces ResizeObserver height recalculation while the carousel is active. */
      queueEqualizeHeights() {
        window.clearTimeout(this.equalizeTimer);
        this.equalizeTimer = window.setTimeout(() => {
          this.equalizeTimer = null;
          if (!this.isConnected) return;
          if (this.isMobileLayout() && this.sliderInstance?.slider) {
            this.equalizeHeights();
          }
        }, 100);
      }

      /** Cancels pending timeouts and animation frames created by this element. */
      cancelQueuedWork() {
        window.clearTimeout(this.resizeTimer);
        window.clearTimeout(this.equalizeTimer);
        this.clearAutoplayResumeTimer();
        if (this.initRaf != null) {
          cancelAnimationFrame(this.initRaf);
          this.initRaf = null;
        }
        this.resizeTimer = null;
        this.equalizeTimer = null;
      }

      /** Retries layout synchronization on the next frame while theme scripts load. */
      queueSyncFrame() {
        if (this.initRaf != null) return;

        this.initRaf = requestAnimationFrame(() => {
          this.initRaf = null;
          this.syncSlider();
        });
      }

      /** Returns the global theme namespace when it is available. */
      get theme() {
        return window.FoxTheme;
      }

      /** Checks whether theme config and carousel helpers are ready to use. */
      isThemeReady() {
        return Boolean(this.theme?.Carousel && this.theme?.config);
      }

      /** Returns a MediaQueryList for the mobile layout, falling back to the theme query string. */
      getMobileMediaQueryList() {
        const configuredMql = this.theme?.config?.mqlMobile;
        if (configuredMql && typeof configuredMql.matches === 'boolean') {
          return configuredMql;
        }

        const query = this.theme?.config?.mediaQueryMobile || MOBILE_LAYOUT_QUERY;
        return window.matchMedia(query);
      }

      /** Reports whether the USP block should use the mobile carousel layout. */
      isMobileLayout() {
        if (this.mql && typeof this.mql.matches === 'boolean') {
          return this.mql.matches;
        }

        return Boolean(this.theme?.config?.mqlMobile);
      }

      /** Sets one host CSS variable so all USP cards share the tallest card height. */
      equalizeHeights() {
        if (!this.list) return;

        const items = this.getOriginalSlides();
        if (items.length < 2) {
          this.clearEqualizedHeights();
          return;
        }

        this.style.removeProperty('--usp-item-min-height');

        let maxHeight = 0;
        items.forEach((item) => {
          maxHeight = Math.max(maxHeight, item.offsetHeight);
        });

        if (maxHeight > 0) {
          this.style.setProperty('--usp-item-min-height', `${maxHeight}px`);
        }

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.update();
        }
      }

      /** Removes the host CSS variable used for equalized card heights. */
      clearEqualizedHeights() {
        this.style.removeProperty('--usp-item-min-height');
      }

      /** Mirrors click activation for Enter keypresses on delegated nav controls. */
      onNavKeypress = (event, button) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        button.click();
      };

      /** Pauses autoplay when users navigate with the arrow controls. */
      onNavClick = () => {
        this.pauseAutoplayForInteraction();
      };

      /** Creates the mobile carousel or updates the existing instance. */
      initSlider() {
        if (!this.isMobileLayout()) {
          this.destroySlider();
          return;
        }

        this.pruneDuplicateSlides();

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.update();
          this.equalizeHeights();
          return;
        }

        const sliderOptions = this.resolveSliderOptions(this.sliderOptions);

        this.wrap.classList.add('swiper');
        this.list.classList.add('swiper-wrapper');
        this.getOriginalSlides().forEach((slide) => {
          slide.classList.add('swiper-slide');
        });

        const additionModules = sliderOptions.autoplay ? [window.FoxTheme.Swiper.Autoplay] : [];
        this.sliderInstance = new window.FoxTheme.Carousel(this.wrap, sliderOptions, additionModules);
        this.sliderInstance.init();
      }

      /** Normalizes carousel state after Swiper has initialized or resized. */
      afterSliderInit() {
        if (!this.sliderInstance?.slider) return;

        const { slider } = this.sliderInstance;
        this.equalizeHeights();
        if (slider.params.loop) {
          slider.slideToLoop(0, 0);
        } else {
          slider.slideTo(0, 0);
        }
        slider.updateSize();
        slider.updateSlides();
        slider.update();
        slider.updateAutoHeight?.(0);
        slider.autoplay?.start?.();
        this.bindAutoplayInteractionHandling(slider);
      }

      /** Recalculates carousel dimensions without changing the active slide. */
      afterSliderResize() {
        if (!this.sliderInstance?.slider) return;

        const { slider } = this.sliderInstance;
        this.equalizeHeights();
        slider.updateSize();
        slider.updateSlides();
        slider.update();
        slider.updateAutoHeight?.(0);
      }

      /** Destroys the carousel and restores static list classes defensively. */
      destroySlider() {
        this.unbindAutoplayInteractionHandling();
        this.clearAutoplayResumeTimer();

        const slider = this.sliderInstance?.slider;
        if (slider) {
          try {
            slider.autoplay?.stop?.();
            slider.loopDestroy?.();
            slider.destroy(true, true);
          } catch (_) {
            /* noop */
          }
          this.sliderInstance = null;
        }

        this.restoreStaticSliderMarkup();
        this.pruneDuplicateSlides();
      }

      /** Returns the original USP slides, excluding Swiper loop clones. */
      getOriginalSlides() {
        if (!this.list) return [];

        return Array.from(
          this.list.querySelectorAll(':scope > .product__usp-item:not(.swiper-slide-duplicate)')
        );
      }

      /** Counts original USP slides before any carousel cloning. */
      countOriginalSlides() {
        return this.getOriginalSlides().length;
      }

      /** Removes loop clones and any extra slides left after a partial destroy. */
      pruneDuplicateSlides() {
        if (!this.list) return;

        this.list.querySelectorAll('.swiper-slide-duplicate').forEach((slide) => slide.remove());

        const slides = this.getOriginalSlides();
        if (this.slideCount > 0 && slides.length > this.slideCount) {
          slides.slice(this.slideCount).forEach((slide) => slide.remove());
        }
      }

      /** Strips Swiper classes and inline styles after destroy. */
      restoreStaticSliderMarkup() {
        if (this.wrap) {
          this.wrap.classList.remove(
            'swiper',
            'swiper-initialized',
            'swiper-horizontal',
            'swiper-backface-hidden'
          );
          this.wrap.removeAttribute('style');
        }

        if (!this.list) return;

        this.list.classList.remove('swiper-wrapper');
        this.list.removeAttribute('style');

        this.getOriginalSlides().forEach((slide) => {
          slide.classList.remove(
            'swiper-slide',
            'swiper-slide-active',
            'swiper-slide-next',
            'swiper-slide-prev',
            'swiper-slide-visible'
          );
          slide.removeAttribute('style');
        });
      }

      /** Parses Liquid-authored carousel options from the data attribute. */
      parseSliderOptions() {
        try {
          return JSON.parse(this.dataset.sliderOptions || '{}');
        } catch (_) {
          return {};
        }
      }

      /** Converts selector strings from Liquid options into element references. */
      resolveSliderOptions(options) {
        const resolved = {
          ...options,
          on: {
            ...options.on,
            init: () => this.afterSliderInit(),
            resize: () => this.afterSliderResize(),
          },
        };

        if (resolved.navigation) {
          resolved.navigation = {
            ...resolved.navigation,
            prevEl: this.querySelector(resolved.navigation.prevEl || '.swiper-button-prev'),
            nextEl: this.querySelector(resolved.navigation.nextEl || '.swiper-button-next'),
          };
        }

        if (resolved.pagination) {
          const paginationEl = this.querySelector(resolved.pagination.el || '.swiper-pagination');
          if (paginationEl) {
            resolved.pagination = {
              ...resolved.pagination,
              el: paginationEl,
            };
          } else {
            delete resolved.pagination;
          }
        }

        if (this.#prefersReducedMotion()) {
          resolved.loop = false;
          delete resolved.autoplay;
        } else if (resolved.autoplay) {
          resolved.autoplay = {
            delay: USP_AUTOPLAY_DELAY_MS,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
            ...resolved.autoplay,
          };
        }

        const slideCount = this.list?.children.length ?? 0;
        if (resolved.loop && slideCount > 0 && slideCount < 4) {
          resolved.loopedSlides = slideCount;
        }

        return resolved;
      }

      #prefersReducedMotion() {
        return Boolean(
          window.FoxTheme?.config?.motionReduced ??
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        );
      }

      bindAutoplayInteractionHandling(slider) {
        this.unbindAutoplayInteractionHandling();
        if (!slider?.autoplay || this.#prefersReducedMotion()) return;

        const pause = () => this.pauseAutoplayForInteraction();
        const events = ['touchStart', 'sliderFirstMove', 'navigationNext', 'navigationPrev'];
        events.forEach((eventName) => slider.on(eventName, pause));
        this.autoplayInteractionBinding = { slider, pause, events };
      }

      unbindAutoplayInteractionHandling() {
        const binding = this.autoplayInteractionBinding;
        if (!binding) return;

        binding.events.forEach((eventName) => binding.slider.off(eventName, binding.pause));
        this.autoplayInteractionBinding = null;
      }

      pauseAutoplayForInteraction() {
        const slider = this.sliderInstance?.slider;
        if (!slider?.autoplay || this.#prefersReducedMotion()) return;

        slider.autoplay.stop();
        this.scheduleAutoplayResume(slider);
      }

      scheduleAutoplayResume(slider) {
        this.clearAutoplayResumeTimer();
        this.autoplayResumeTimer = window.setTimeout(() => {
          this.autoplayResumeTimer = null;
          if (!this.isConnected || !this.isMobileLayout()) return;
          if (this.#prefersReducedMotion()) return;
          slider.autoplay?.start?.();
        }, USP_AUTOPLAY_RESUME_MS);
      }

      clearAutoplayResumeTimer() {
        if (this.autoplayResumeTimer == null) return;
        window.clearTimeout(this.autoplayResumeTimer);
        this.autoplayResumeTimer = null;
      }
    }
  );
}
